/**
 * HTTP Errors
 * 
 * Non-HTTP errors - No Http status codes
 * 
 * idempotent HTTP methods - GET, HEAD, PUT, DELETE, OPTIONS
 * 
 * Non-idempotent HTTP methods - POST, PATCH
 * 
 * CORs error
 * 
 * Network Error - ERR_NETWORK
 * https://github.com/axios/axios/issues/5644
 * https://github.com/axios/axios/issues/5644#issuecomment-1828658119
 * 
 */
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { exponentialBackoff, sleep } from '../utils/utils';

interface CustomAxiosRequestConfig extends AxiosRequestConfig {
    metadata?: {
        requestTimeoutId: NodeJS.Timeout;
    };
    __retryCount?: number;
}

interface CustomAxiosRetryConfig {
    timeout?: number;
    retryDelay?: number;
    maxRetries?: number;
    headers?: Record<string, string>;
    customRetryCondition?: boolean;
    onMaxRetryTimesExceeded?: (error: AxiosError, retryCount: number) => Promise<void> | void;
    onAPIError?: (error: AxiosError) => void;
}

const DEFAULT_CONFIG = {
    TIMEOUT: 30000,
    RETRY_DELAY: 1000, // lessthan 1000 is good for exponential backoff - // 2^0 = 1, 2^1 = 2, 2^2 = 4,
    MAX_RETRIES: 3,
} as const;

const retryableErrorsInBrowser = [
    'ECONNABORTED', // VPN disconnect or timeout or manual abort or Cors
    'ETIMEDOUT', // Temporary Network Error | Connection timeout 
    'ERR_NETWORK', // Non-Retryable/Ambiguous | Generic network error — often from client-side/network issues
    'ERR_CANCELED' // Non-Retryable/Ambiguous | Request was canceled (manual abort, page navigation)
]

export function isRetryableError(error: AxiosError, customRetryCondition: Boolean): boolean {
    if (error.code && retryableErrorsInBrowser.includes(error.code)) {
        return true;
    }

    // this customRetryCondition is used to handle retry all error codes except 200. 
    // Server send 550 code if IP is not allowed.
    if (customRetryCondition &&
        error.code !== 'ECONNABORTED' &&
        (!error.response || error.response.status !== 200)) {
        return true;
    }

    // default retry condition
    if (error.code !== 'ECONNABORTED' && // this will handle CORS error
        (!error.response ||
            (error.response.status === 429) ||
            (error.response.status >= 500 && error.response.status <= 599))) {
        return true;
    }

    return false;
}

export function createAxiosInstance(config: CustomAxiosRetryConfig = {}): AxiosInstance {
    const requestTimeout = config.timeout || DEFAULT_CONFIG.TIMEOUT;
    const maxRetries = config.maxRetries || DEFAULT_CONFIG.MAX_RETRIES;
    const retryDelay = config.retryDelay || DEFAULT_CONFIG.RETRY_DELAY;
    const customRetryCondition = config.customRetryCondition || false;
    const maxRetryCallback = config.onMaxRetryTimesExceeded || (() => { });
    const axiosInstance = axios.create({ headers: { 'Content-Type': 'application/json', ...config.headers } });

    let requestTimeoutIdForCancelledRequests = null as NodeJS.Timeout | null;

    // request interceptor
    axiosInstance.interceptors.request.use(
        (config: CustomAxiosRequestConfig) => {
            // Create AbortController for each request. cancelToken is deprecated
            // This is for long pending requests
            const controller = new AbortController();
            const requestTimeoutId = setTimeout(() => {
                controller.abort();
                console.log("VPN_DISCONNECT_CHECK - Long pending request cancelled");
            }, requestTimeout);

            // Store the requestTimeoutId in config for cleanup if we got some status code
            config.metadata = { requestTimeoutId };
            requestTimeoutIdForCancelledRequests = requestTimeoutId;
            config.signal = controller.signal;

            return config;
        }, (error) => {
            console.log(`VPN_DISCONNECT_CHECK - Request Error: ${error}`);
            Promise.reject(error)
        }
    )

    // response interceptor
    axiosInstance.interceptors.response.use(
        (response) => {
            const config = response.config as CustomAxiosRequestConfig;

            // Clear the timeout if the request was successful
            if (config.metadata && config.metadata.requestTimeoutId) {
                clearTimeout(config.metadata.requestTimeoutId); // if this fail's, it will lead to memory leak, keep an 👀 man!
            }

            return response;
        },
        async (error: AxiosError) => {
            const config = error.config as CustomAxiosRequestConfig;
            const url = config?.baseURL + config?.url;

            if (error.message === 'canceled') {
                console.log(`VPN_DISCONNECT_CHECK - ${url} Request Timeout - canceled | ${error}`);
                clearTimeout(requestTimeoutIdForCancelledRequests as NodeJS.Timeout);
                // retry logic for the canceled requests cannot be handled here, since i am not able to __retryCount property.
                // since for cancelled requests, the axios config object won't be available

                // so logic move to it parent function.
                return Promise.reject(error);
            }
            // Clear the timeout if the request was successful
            if (config?.metadata && config?.metadata.requestTimeoutId) {
                console.log(`VPN_DISCONNECT_CHECK - ${url} Request Timeout ${error}`);
                clearTimeout(config.metadata.requestTimeoutId); // if this fail's, it will lead to memory leak, keep an 👀 man!
            }

            if (isRetryableError(error, customRetryCondition)) {
                config.__retryCount = (config?.__retryCount || 0) + 1;

                if (config.__retryCount < maxRetries) {
                    const delay = exponentialBackoff(config.__retryCount, retryDelay);

                    console.log(`VPN_DISCONNECT_CHECK - ${url} Retrying... (${config.__retryCount}/${maxRetries})`);
                    await sleep(delay);

                    return axiosInstance(config);
                } else {
                    // Call the callback when max retries are exceeded
                    maxRetryCallback(error, config.__retryCount);
                }
            }

            // For non-retryable errors, no retry
            return Promise.reject(error);
        }
    )

    return axiosInstance;
}
